import {
  Badge,
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  Input,
  Select,
  Spinner,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useToast,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useMemo, useState } from "react"
import { OpenAPI } from "../../../client"
import { TiptapEditor } from "../../../components/Common/TiptapEditor"

export const Route = createFileRoute("/_layout/admin/marketing-templates")({
  component: MarketingTemplatesAdminPage,
})

type TemplateItem = {
  id: string
  name: string
  slug: string
  subject_template: string
  body_html_template: string
  body_text_template: string
  supported_variables: string[] | null
  status: string
  created_at: string
  updated_at: string
}

type TemplateListResponse = {
  items: TemplateItem[]
  total: number
  limit: number
  offset: number
}

type LeadOption = {
  id: string
  email: string
  first_name: string
  last_name: string
  company_name: string
}

type TemplatePayload = {
  name: string
  slug: string
  subject_template: string
  body_html_template: string
  body_text_template: string
  supported_variables: string[]
  status: string
}

type TemplatePreviewResponse = {
  subject: string
  html: string
  text: string
  vars: Record<string, string>
}

type SendTestPayload = {
  to: string
  lead_id?: string
  variables: Record<string, string>
  tracked_links: Record<string, string>
  unsubscribe_url: string
}

type TestSendResult = {
  mode: "custom" | "lead"
  recipient: string
  unsubscribeKind: "real" | "preview"
  providerMessageId: string
  subject: string
}

const defaultTemplateForm: TemplatePayload = {
  name: "",
  slug: "",
  subject_template: "",
  body_html_template:
    '<p>Witaj, {{first_name}},</p><p>Możemy pomóc {{company_name}} zwiększyć efektywność magazynu.</p><p><a href="{{unsubscribe_url}}">Wypisz się</a></p>',
  body_text_template:
    "Witaj, {{first_name}},\n\nMożemy pomóc {{company_name}} zwiększyć efektywność magazynu.\n\nWypisz się: {{unsubscribe_url}}",
  supported_variables: [
    "first_name",
    "last_name",
    "company_name",
    "email",
    "unsubscribe_url",
    "website_url",
  ],
  status: "active",
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = localStorage.getItem("access_token") || ""
  const res = await fetch(`${OpenAPI.BASE}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init?.headers ?? {}),
    },
  })
  if (!res.ok) {
    const text = await res.text()
    try {
      const parsed = JSON.parse(text)
      throw new Error(
        parsed?.error ||
          parsed?.detail ||
          parsed?.message ||
          `HTTP ${res.status}`,
      )
    } catch {
      throw new Error(text || `HTTP ${res.status}`)
    }
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

function getTemplateStatusColor(status?: string) {
  switch (status) {
    case "active":
      return "green"
    case "archived":
      return "gray"
    default:
      return "gray"
  }
}

function getTemplateStatusLabel(status?: string) {
  switch (status) {
    case "active":
      return "Aktywny"
    case "archived":
      return "Archiwalny"
    default:
      return status || "Nieznany"
  }
}

function normalizeSlug(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
}

function parseTrackedLinksInput(value: string) {
  return Object.fromEntries(
    value
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const separatorIndex = line.indexOf("=")
        if (separatorIndex < 0) {
          return [line.trim(), ""]
        }
        return [
          line.slice(0, separatorIndex).trim(),
          line.slice(separatorIndex + 1).trim(),
        ]
      })
      .filter(([key, url]) => key && url),
  )
}

function MarketingTemplatesAdminPage() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const panelBg = useColorModeValue("white", "gray.800")
  const mutedTextColor = useColorModeValue("gray.500", "gray.300")
  const tableHeadBg = useColorModeValue("gray.50", "gray.700")
  const tableRowHoverBg = useColorModeValue("gray.50", "gray.700")
  const previewBg = useColorModeValue("gray.50", "gray.900")

  const [q, setQ] = useState("")
  const [status, setStatus] = useState("")
  const [editingId, setEditingId] = useState<string>("")
  const [variablesInput, setVariablesInput] = useState(
    defaultTemplateForm.supported_variables.join(", "),
  )
  const [testMode, setTestMode] = useState<"custom" | "lead">("custom")
  const [testEmail, setTestEmail] = useState("")
  const [testLeadId, setTestLeadId] = useState("")
  const [testFirstName, setTestFirstName] = useState("Anna")
  const [testLastName, setTestLastName] = useState("Nowak")
  const [testCompanyName, setTestCompanyName] = useState("Nordic Fulfillment")
  const [testUnsubscribeUrl, setTestUnsubscribeUrl] = useState("")
  const [testTrackedLinksInput, setTestTrackedLinksInput] = useState("")
  const [lastTestSendResult, setLastTestSendResult] =
    useState<TestSendResult | null>(null)
  const [form, setForm] = useState<TemplatePayload>(defaultTemplateForm)
  const testTrackedLinks = useMemo(
    () => parseTrackedLinksInput(testTrackedLinksInput),
    [testTrackedLinksInput],
  )

  const queryString = useMemo(() => {
    const params = new URLSearchParams()
    params.set("limit", "200")
    if (q.trim()) params.set("q", q.trim())
    if (status) params.set("status", status)
    return params.toString()
  }, [q, status])

  const listQuery = useQuery({
    queryKey: ["lms", "marketing", "templates", queryString],
    queryFn: () =>
      apiFetch<TemplateListResponse>(
        `/v1/lms/marketing/templates?${queryString}`,
      ),
  })

  const leadsQuery = useQuery({
    queryKey: ["lms", "marketing", "templates", "test-leads"],
    queryFn: () =>
      apiFetch<{
        items: LeadOption[]
        total: number
        limit: number
        offset: number
      }>("/v1/lms/marketing/leads?limit=100&status=active&unsubscribed=false"),
  })

  const previewQuery = useQuery({
    queryKey: [
      "lms",
      "marketing",
      "templates",
      "preview",
      form,
      testFirstName,
      testLastName,
      testCompanyName,
      testEmail,
      testUnsubscribeUrl,
      testTrackedLinksInput,
    ],
    queryFn: () =>
      apiFetch<TemplatePreviewResponse>("/v1/lms/marketing/templates/preview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          subject_template: form.subject_template,
          body_html_template: form.body_html_template,
          body_text_template: form.body_text_template,
          variables: {
            first_name: testFirstName,
            last_name: testLastName,
            company_name: testCompanyName,
            email: testEmail || "anna.nowak@example.com",
            unsubscribe_url: testUnsubscribeUrl,
          },
          tracked_links: testTrackedLinks,
        }),
      }),
    staleTime: 0,
  })

  const activeCount = useMemo(
    () =>
      (listQuery.data?.items ?? []).filter((item) => item.status === "active")
        .length,
    [listQuery.data?.items],
  )

  const upsertMutation = useMutation({
    mutationFn: (payload: TemplatePayload) =>
      apiFetch<TemplateItem>(
        editingId
          ? `/v1/lms/marketing/templates/${editingId}`
          : "/v1/lms/marketing/templates",
        {
          method: editingId ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      ),
    onSuccess: () => {
      toast({
        title: editingId
          ? "Szablon został zaktualizowany"
          : "Szablon został utworzony",
        status: "success",
        duration: 2500,
      })
      queryClient.invalidateQueries({
        queryKey: ["lms", "marketing", "templates"],
      })
      startCreate()
    },
    onError: (error: any) => {
      toast({
        title: "Nie udało się zapisać",
        description: error?.message ?? "Błąd",
        status: "error",
        duration: 4000,
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (templateId: string) =>
      apiFetch<{ status: string }>(
        `/v1/lms/marketing/templates/${templateId}`,
        {
          method: "DELETE",
        },
      ),
    onSuccess: () => {
      toast({
        title: "Szablon został usunięty",
        status: "warning",
        duration: 2500,
      })
      queryClient.invalidateQueries({
        queryKey: ["lms", "marketing", "templates"],
      })
      if (editingId) startCreate()
    },
    onError: (error: any) => {
      toast({
        title: "Nie udało się usunąć",
        description: error?.message ?? "Błąd",
        status: "error",
        duration: 4000,
      })
    },
  })

  const testSendMutation = useMutation({
    mutationFn: (payload: SendTestPayload) => {
      if (!editingId) {
        throw new Error("Zapisz szablon przed wysłaniem wiadomości testowej")
      }
      return apiFetch<{
        status: string
        provider_message_id: string
        subject: string
      }>(`/v1/lms/marketing/templates/${editingId}/test-send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
    },
    onSuccess: (result) => {
      toast({
        title: "Wiadomość testowa została wysłana",
        description: `ID wiadomości: ${result.provider_message_id}`,
        status: "success",
        duration: 5000,
      })
      setLastTestSendResult({
        mode: testMode,
        recipient: testEmail,
        unsubscribeKind: testMode === "lead" ? "real" : "preview",
        providerMessageId: result.provider_message_id,
        subject: result.subject,
      })
    },
    onError: (error: any) => {
      toast({
        title: "Nie udało się wysłać testu",
        description: error?.message ?? "Błąd",
        status: "error",
        duration: 4000,
      })
    },
  })

  function startCreate() {
    setEditingId("")
    setVariablesInput(defaultTemplateForm.supported_variables.join(", "))
    setTestMode("custom")
    setTestLeadId("")
    setTestEmail("")
    setTestFirstName("Anna")
    setTestLastName("Nowak")
    setTestCompanyName("Nordic Fulfillment")
    setTestUnsubscribeUrl("")
    setTestTrackedLinksInput("")
    setLastTestSendResult(null)
    setForm(defaultTemplateForm)
  }

  function startEdit(template: TemplateItem) {
    const vars = Array.isArray(template.supported_variables)
      ? template.supported_variables
      : defaultTemplateForm.supported_variables
    setEditingId(template.id)
    setVariablesInput(vars.join(", "))
    setForm({
      name: template.name,
      slug: template.slug,
      subject_template: template.subject_template,
      body_html_template: template.body_html_template,
      body_text_template: template.body_text_template ?? "",
      supported_variables: vars,
      status: template.status ?? "active",
    })
  }

  return (
    <Container maxW="9xl" py={4}>
      <VStack align="stretch" spacing={4}>
        <Box borderWidth="1px" borderRadius="xl" p={5} bg={panelBg}>
          <Heading size="lg">Szablony e-mail</Heading>
          <Text color={mutedTextColor} mt={1}>
            Szablony cold maili z personalizacją, edytorem HTML i podglądem
            renderowania.
          </Text>
          <Grid
            templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
            gap={3}
            mt={4}
          >
            <Box borderWidth="1px" borderRadius="lg" p={3}>
              <Stat>
                <StatLabel>Załadowane szablony</StatLabel>
                <StatNumber>{listQuery.data?.total ?? 0}</StatNumber>
                <StatHelpText>W zakresie bieżącego filtra</StatHelpText>
              </Stat>
            </Box>
            <Box borderWidth="1px" borderRadius="lg" p={3}>
              <Stat>
                <StatLabel>Aktywne szablony</StatLabel>
                <StatNumber color={activeCount > 0 ? "green.500" : "gray.700"}>
                  {activeCount}
                </StatNumber>
                <StatHelpText>Gotowe do użycia w kampaniach</StatHelpText>
              </Stat>
            </Box>
          </Grid>
        </Box>

        <Box borderWidth="1px" borderRadius="xl" p={4}>
          <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={3}>
            <GridItem>
              <FormControl>
                <FormLabel>Szukaj</FormLabel>
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="nazwa / slug / temat"
                />
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel>Status</FormLabel>
                <Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="">Wszystkie</option>
                  <option value="active">Aktywny</option>
                  <option value="archived">Archiwalny</option>
                </Select>
              </FormControl>
            </GridItem>
            <GridItem alignSelf="end">
              <Button w="full" onClick={startCreate}>
                Nowy szablon
              </Button>
            </GridItem>
          </Grid>
        </Box>

        <Box borderWidth="1px" borderRadius="xl" p={4}>
          <Heading size="md" mb={4}>
            {editingId ? "Edytuj szablon" : "Utwórz szablon"}
          </Heading>
          <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={4}>
            <GridItem>
              <VStack align="stretch" spacing={3}>
                <FormControl isRequired>
                  <FormLabel>Nazwa</FormLabel>
                  <Input
                    value={form.name}
                    onChange={(e) =>
                      setForm((current) => ({
                        ...current,
                        name: e.target.value,
                        slug: current.slug
                          ? current.slug
                          : normalizeSlug(e.target.value),
                      }))
                    }
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Slug</FormLabel>
                  <Input
                    value={form.slug}
                    onChange={(e) =>
                      setForm((current) => ({
                        ...current,
                        slug: normalizeSlug(e.target.value),
                      }))
                    }
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Szablon tematu</FormLabel>
                  <Input
                    value={form.subject_template}
                    onChange={(e) =>
                      setForm((current) => ({
                        ...current,
                        subject_template: e.target.value,
                      }))
                    }
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Obsługiwane zmienne</FormLabel>
                  <Input
                    value={variablesInput}
                    onChange={(e) => setVariablesInput(e.target.value)}
                    placeholder="first_name, last_name, company_name, email, unsubscribe_url, website_url"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Status</FormLabel>
                  <Select
                    value={form.status}
                    onChange={(e) =>
                      setForm((current) => ({
                        ...current,
                        status: e.target.value,
                      }))
                    }
                  >
                    <option value="active">Aktywny</option>
                    <option value="archived">Archiwalny</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel>Wersja tekstowa</FormLabel>
                  <Textarea
                    rows={8}
                    value={form.body_text_template}
                    onChange={(e) =>
                      setForm((current) => ({
                        ...current,
                        body_text_template: e.target.value,
                      }))
                    }
                  />
                </FormControl>
              </VStack>
            </GridItem>

            <GridItem>
              <VStack align="stretch" spacing={3}>
                <FormControl isRequired>
                  <FormLabel>Edytor HTML</FormLabel>
                  <Text fontSize="sm" color={mutedTextColor} mb={2}>
                    Dla śledzonego przycisku zaznacz tekst, kliknij Link i wklej
                    {
                      " `{{website_url}}`, `{{unsubscribe_url}}` albo dowolny placeholder z kampanii, np. `{{demo_url}}`."
                    }
                  </Text>
                  <TiptapEditor
                    value={form.body_html_template}
                    onChange={(value) =>
                      setForm((current) => ({
                        ...current,
                        body_html_template: value,
                      }))
                    }
                    maxHeight="380px"
                  />
                </FormControl>
                <HStack justify="flex-end">
                  <Button variant="ghost" onClick={startCreate}>
                    Resetuj
                  </Button>
                  <Button
                    colorScheme="green"
                    onClick={() =>
                      upsertMutation.mutate({
                        ...form,
                        supported_variables: variablesInput
                          .split(",")
                          .map((item) => item.trim())
                          .filter(Boolean),
                      })
                    }
                    isLoading={upsertMutation.isPending}
                  >
                    {editingId ? "Zaktualizuj szablon" : "Utwórz szablon"}
                  </Button>
                </HStack>
              </VStack>
            </GridItem>
          </Grid>
        </Box>

        <Grid
          templateColumns={{ base: "1fr", lg: "minmax(0, 1fr) minmax(0, 1fr)" }}
          gap={4}
          alignItems="start"
        >
          <Box borderWidth="1px" borderRadius="xl" p={4} minW={0}>
            <Heading size="md" mb={4}>
              Podgląd
            </Heading>
            {previewQuery.isPending ? (
              <Spinner />
            ) : (
              <VStack align="stretch" spacing={3}>
                <Box borderWidth="1px" borderRadius="lg" p={3} bg={previewBg}>
                  <Text fontSize="sm" color={mutedTextColor}>
                    Temat
                  </Text>
                  <Text fontWeight="semibold" wordBreak="break-word">
                    {previewQuery.data?.subject || "—"}
                  </Text>
                </Box>
                <Box borderWidth="1px" borderRadius="lg" p={3} bg={previewBg}>
                  <Text fontSize="sm" color={mutedTextColor} mb={2}>
                    Podgląd HTML
                  </Text>
                  <Box
                    as="iframe"
                    title="Podgląd HTML"
                    minW={0}
                    minH="320px"
                    width="100%"
                    overflowX="auto"
                    className="marketing-template-preview"
                    sandbox=""
                    srcDoc={previewQuery.data?.html || ""}
                  />
                </Box>
                <Box borderWidth="1px" borderRadius="lg" p={3} bg={previewBg}>
                  <Text fontSize="sm" color={mutedTextColor} mb={2}>
                    Podgląd tekstu
                  </Text>
                  <Text whiteSpace="pre-wrap" wordBreak="break-word">
                    {previewQuery.data?.text || "—"}
                  </Text>
                </Box>
              </VStack>
            )}
          </Box>

          <Box borderWidth="1px" borderRadius="xl" p={4} minW={0}>
            <Heading size="md" mb={4}>
              Wysyłka testowa
            </Heading>
            <VStack align="stretch" spacing={3} mb={6}>
              <Text color={mutedTextColor}>
                Wyślij jedną prawdziwą wiadomość bez uruchamiania kampanii.
                Wybierz, czy chcesz testować na ręcznych zmiennych, czy na
                prawdziwym leadzie z bazy.
              </Text>
              <FormControl>
                <FormLabel>Tryb testu</FormLabel>
                <Select
                  value={testMode}
                  onChange={(e) =>
                    setTestMode(e.target.value === "lead" ? "lead" : "custom")
                  }
                >
                  <option value="custom">
                    dowolny odbiorca i ręczne zmienne
                  </option>
                  <option value="lead">istniejący lead z bazy danych</option>
                </Select>
              </FormControl>

              {testMode === "lead" ? (
                <FormControl>
                  <FormLabel>Lead</FormLabel>
                  <Select
                    value={testLeadId}
                    onChange={(e) => {
                      const nextLeadId = e.target.value
                      setTestLeadId(nextLeadId)
                      const selected = (leadsQuery.data?.items ?? []).find(
                        (item) => item.id === nextLeadId,
                      )
                      if (selected) {
                        setTestEmail(selected.email || "")
                        setTestFirstName(selected.first_name || "Lead")
                        setTestLastName(selected.last_name || "")
                        setTestCompanyName(
                          selected.company_name || "Przykładowa firma",
                        )
                      }
                    }}
                  >
                    <option value="">Wybierz lead</option>
                    {(leadsQuery.data?.items ?? []).map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.email}
                        {item.company_name ? ` (${item.company_name})` : ""}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <>
                  <FormControl>
                    <FormLabel>E-mail odbiorcy</FormLabel>
                    <Input
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder="test@example.com"
                    />
                  </FormControl>
                  <Grid
                    templateColumns={{ base: "1fr", md: "1fr 1fr" }}
                    gap={3}
                  >
                    <GridItem>
                      <FormControl>
                        <FormLabel>Imię</FormLabel>
                        <Input
                          value={testFirstName}
                          onChange={(e) => setTestFirstName(e.target.value)}
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem>
                      <FormControl>
                        <FormLabel>Nazwisko</FormLabel>
                        <Input
                          value={testLastName}
                          onChange={(e) => setTestLastName(e.target.value)}
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem colSpan={{ base: 1, md: 2 }}>
                      <FormControl>
                        <FormLabel>Nazwa firmy</FormLabel>
                        <Input
                          value={testCompanyName}
                          onChange={(e) => setTestCompanyName(e.target.value)}
                        />
                      </FormControl>
                    </GridItem>
                  </Grid>
                </>
              )}

              <FormControl>
                <FormLabel>URL wypisania</FormLabel>
                <Input
                  value={testUnsubscribeUrl}
                  onChange={(e) => setTestUnsubscribeUrl(e.target.value)}
                  isDisabled={testMode === "lead"}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Śledzone linki do testu</FormLabel>
                <Textarea
                  rows={4}
                  value={testTrackedLinksInput}
                  onChange={(e) => setTestTrackedLinksInput(e.target.value)}
                  placeholder={
                    "demo_url=https://example.com/demo\npricing_url=https://example.com/pricing"
                  }
                />
                <Text mt={2} fontSize="sm" color={mutedTextColor}>
                  Jeden wiersz = `placeholder_url=https://docelowy-adres`. W
                  preview zobaczysz podstawienie URL, a w trybie `lead` backend
                  wygeneruje prawdziwy tracked redirect dla każdego z tych
                  placeholderów.
                </Text>
              </FormControl>

              <HStack justify="space-between" align="flex-start">
                <Text fontSize="sm" color={mutedTextColor}>
                  {!editingId
                    ? "Najpierw zapisz szablon. Wysyłka testowa działa tylko dla zapisanych szablonów."
                    : testMode === "lead"
                      ? "Wiadomość użyje prawdziwych danych leada z bazy, a backend wygeneruje prawdziwy token wypisania."
                      : "Wiadomość użyje ręcznych zmiennych wprowadzonych wyżej. W tym trybie URL wypisania jest tylko testową wartością do podglądu."}
                </Text>
                <Button
                  variant="outline"
                  isDisabled={
                    !editingId ||
                    !testEmail ||
                    (testMode === "lead" && !testLeadId)
                  }
                  isLoading={testSendMutation.isPending}
                  onClick={() =>
                    testSendMutation.mutate({
                      to: testEmail,
                      lead_id: testMode === "lead" ? testLeadId : undefined,
                      variables:
                        testMode === "lead"
                          ? {}
                          : {
                              first_name: testFirstName,
                              last_name: testLastName,
                              company_name: testCompanyName,
                              email: testEmail,
                              unsubscribe_url: testUnsubscribeUrl,
                            },
                      tracked_links: testTrackedLinks,
                      unsubscribe_url:
                        testMode === "lead" ? "" : testUnsubscribeUrl,
                    })
                  }
                >
                  Wiadomość testowa
                </Button>
              </HStack>

              {lastTestSendResult ? (
                <Box borderWidth="1px" borderRadius="lg" p={3} bg={previewBg}>
                  <VStack align="stretch" spacing={2}>
                    <Heading size="sm">Ostatnia wysyłka testowa</Heading>
                    <Text fontSize="sm">
                      Odbiorca: <strong>{lastTestSendResult.recipient}</strong>
                    </Text>
                    <Text fontSize="sm">
                      Tryb:{" "}
                      <strong>
                        {lastTestSendResult.mode === "lead"
                          ? "lead"
                          : "dowolny"}
                      </strong>
                    </Text>
                    <Text fontSize="sm">
                      Link wypisania:{" "}
                      <strong>
                        {lastTestSendResult.unsubscribeKind === "real"
                          ? "prawdziwy token wygenerowany przez backend"
                          : "tylko testowy URL do podglądu"}
                      </strong>
                    </Text>
                    <Text fontSize="sm">
                      Temat: <strong>{lastTestSendResult.subject}</strong>
                    </Text>
                    <Text fontSize="sm">
                      ID wiadomości:{" "}
                      <strong>{lastTestSendResult.providerMessageId}</strong>
                    </Text>
                  </VStack>
                </Box>
              ) : null}
            </VStack>

            <Heading size="md" mb={4}>
              Lista szablonów
            </Heading>
            {listQuery.isPending ? (
              <Spinner />
            ) : (
              <TableContainer overflowX="auto">
                <Table size="sm" sx={{ tableLayout: "fixed", width: "100%" }}>
                  <Thead bg={tableHeadBg}>
                    <Tr>
                      <Th>Nazwa</Th>
                      <Th>Slug</Th>
                      <Th>Status</Th>
                      <Th>Zaktualizowano</Th>
                      <Th textAlign="right">Akcje</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {(listQuery.data?.items ?? []).map((item) => (
                      <Tr key={item.id} _hover={{ bg: tableRowHoverBg }}>
                        <Td maxW="280px" wordBreak="break-word">
                          {item.name}
                        </Td>
                        <Td maxW="220px" wordBreak="break-word">
                          {item.slug}
                        </Td>
                        <Td>
                          <Badge
                            colorScheme={getTemplateStatusColor(item.status)}
                          >
                            {getTemplateStatusLabel(item.status)}
                          </Badge>
                        </Td>
                        <Td>{new Date(item.updated_at).toLocaleString()}</Td>
                        <Td>
                          <HStack justify="flex-end">
                            <Button
                              size="xs"
                              variant="outline"
                              onClick={() => startEdit(item)}
                            >
                              Edytuj
                            </Button>
                            <Button
                              size="xs"
                              colorScheme="red"
                              variant="ghost"
                              onClick={() => deleteMutation.mutate(item.id)}
                              isLoading={deleteMutation.isPending}
                            >
                              Usuń
                            </Button>
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Grid>
      </VStack>
    </Container>
  )
}
